import React, { HTMLAttributes } from "react"
import Image, { ImageProps } from "next/image"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"
import { Tag, type TagProps } from "@/components/ui/atoms"

const PictureWrapperVariants = cva("max-h-full max-w-full relative", {
  variants: {
    variant: {
      default: "aspect-auto",
      vertical: "w-[343px] sm:w-[360px] lg:w-[490px]",
      horizontalL: "w-[343px] sm:w-[360px] lg:w-[530px]",
      headerArticle: "w-[343px] sm:w-[360px] lg:w-[880px]",
      horizontalS: "w-full lg:w-[171px] xl:w-[213px] 2xl:w-[256px]",
      articleCard: "xl:w-[248px]",
    },
  },
  defaultVariants: {
    variant: "default",
  },
})

type TFillVariant =
  | "vertical"
  | "horizontalL"
  | "headerArticle"
  | "horizontalS"
  | "articleCard"

// Matches the widths declared above so the browser only downloads an image
// as large as the slot it's actually rendered into, instead of Next's
// fallback of assuming the image spans the full viewport.
const pictureSizesByVariant: Record<TFillVariant, string> = {
  vertical: "(min-width: 1024px) 490px, (min-width: 640px) 360px, 343px",
  horizontalL: "(min-width: 1024px) 530px, (min-width: 640px) 360px, 343px",
  headerArticle: "(min-width: 1024px) 880px, (min-width: 640px) 360px, 343px",
  horizontalS:
    "(min-width: 1536px) 256px, (min-width: 1280px) 213px, (min-width: 1024px) 171px, 100vw",
  articleCard: "(min-width: 1280px) 248px, 100vw",
}

type BaseProps = VariantProps<typeof PictureWrapperVariants> & {
  src: ImageProps["src"]
  alt: ImageProps["alt"]
  className?: string
  tagIcon?: TagProps["icon"]
  tagLabel?: TagProps["label"]
  alignLeft?: boolean
  width?: ImageProps["width"]
  height?: ImageProps["height"]
  isRounded?: boolean
  isSemiCircle?: boolean
} & HTMLAttributes<HTMLDivElement>

type TPictureWithWidthHeight = Omit<BaseProps, "variant"> & {
  variant?: "default"
  width: number
  height: number
}

type TPictureWhithoutWidthAndHeight = Omit<BaseProps, "variant"> & {
  variant: Exclude<BaseProps["variant"], "default">
  width?: never
  height?: never
}

type TPictureProps = Omit<BaseProps, "variant"> & {
  src: string
  alt: string
  className?: string
} & (TPictureWithWidthHeight | TPictureWhithoutWidthAndHeight)

const Picture = ({
  tagIcon,
  tagLabel,
  alignLeft,
  variant,
  src,
  alt,
  width,
  height,
  className,
  isRounded = false,
  isSemiCircle = false,
}: TPictureProps) => {
  const ratio =
    variant === "default" || variant === undefined
      ? "w-full h-full"
      : `aspect-${variant}`
  return (
    <div className={cn(PictureWrapperVariants({ variant }), className)}>
      {tagLabel ? (
        <div
          className={cn(
            "absolute inset-x-0 bottom-0 z-10 inline-flex flex-wrap items-center justify-start",
            { "justify-end": !alignLeft }
          )}
        >
          <Tag
            variant="accent"
            className={
              isSemiCircle
                ? alignLeft
                  ? "rounded-b-0 rounded-tl-0"
                  : "rounded-r-0 rounded-bl-0"
                : isRounded
                  ? "rounded-br-0 rounded-tl-0"
                  : ""
            }
            icon={tagIcon}
            label={tagLabel}
          />
        </div>
      ) : null}
      <div
        className={cn(
          "relative",
          ratio,
          variant === "default" || (variant === undefined && height)
            ? `h-[${height}px]`
            : "",
          variant === "default" || (variant === undefined && width)
            ? `w-[${width}px]`
            : ""
        )}
      >
        <Image
          src={src}
          alt={alt ? alt : "No alt text defined"}
          width={width || undefined}
          height={height || undefined}
          fill={!width || !height}
          sizes={
            width && height
              ? undefined
              : (pictureSizesByVariant[variant as TFillVariant] ?? "100vw")
          }
          style={{ objectFit: "cover" }}
          className={cn(
            "size-full",
            isRounded ? "rounded-100" : "",
            isSemiCircle ? "rounded-t-[1000px]" : "",
            variant === "articleCard" ? "rounded-t-100 xl:rounded-l-100" : ""
          )}
        />
      </div>
    </div>
  )
}

export { Picture, PictureWrapperVariants, type TPictureProps }
